import { useTheme } from "next-themes"
import { Toaster as Sonner, toast } from "sonner"
import { useIsMobile } from "@/hooks/use-mobile"
import { useEffect } from "react"

type ToasterProps = React.ComponentProps<typeof Sonner>

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme()
  const isMobile = useIsMobile()

  // Add mobile swipe-to-dismiss functionality
  useEffect(() => {
    if (!isMobile) return

    let startY = 0
    let currentY = 0
    let isDragging = false
    let toastElement: HTMLElement | null = null

    const handleTouchStart = (e: TouchEvent) => {
      const target = e.target as HTMLElement
      toastElement = target.closest('[data-sonner-toast]')
      
      if (toastElement) {
        startY = e.touches[0].clientY
        isDragging = true
        toastElement.style.transition = 'none'
      }
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging || !toastElement) return
      
      currentY = e.touches[0].clientY
      const diff = startY - currentY // Positive when swiping up
      
      if (diff > 0) {
        toastElement.style.transform = `translateY(-${diff}px)`
        toastElement.style.opacity = String(Math.max(0.3, 1 - diff / 100))
      }
    }

    const handleTouchEnd = () => {
      if (!isDragging || !toastElement) return
      
      const diff = startY - currentY
      
      if (diff > 50) {
        // Dismiss the toast
        toastElement.style.transition = 'all 0.2s ease-out'
        toastElement.style.transform = 'translateY(-100px)'
        toastElement.style.opacity = '0'
        
        // Find and click the close button
        const closeButton = toastElement.querySelector('[data-close-button]') as HTMLButtonElement
        if (closeButton) {
          setTimeout(() => closeButton.click(), 150)
        }
      } else {
        // Reset position
        toastElement.style.transition = 'all 0.2s ease-out'
        toastElement.style.transform = 'translateY(0)'
        toastElement.style.opacity = '1'
      }
      
      isDragging = false
      currentY = 0
      startY = 0
      toastElement = null
    }

    document.addEventListener('touchstart', handleTouchStart, { passive: false })
    document.addEventListener('touchmove', handleTouchMove, { passive: false })
    document.addEventListener('touchend', handleTouchEnd)

    return () => {
      document.removeEventListener('touchstart', handleTouchStart)
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleTouchEnd)
    }
  }, [isMobile])

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      position={isMobile ? "top-center" : "bottom-right"}
      offset={isMobile ? "calc(env(safe-area-inset-top) + 3rem)" : undefined}
      toastOptions={{
        classNames: {
          toast: `group toast group-[.toaster]:bg-background/95 group-[.toaster]:backdrop-blur-sm group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg ${
            isMobile ? "group-[.toaster]:mx-4 group-[.toaster]:max-w-sm" : ""
          }`,
          description: "group-[.toast]:text-muted-foreground",
          actionButton:
            "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton:
            "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
          closeButton: "group-[.toast]:absolute group-[.toast]:right-2 group-[.toast]:top-2",
        },
      }}
      closeButton={true}
      {...props}
    />
  )
}

export { Toaster, toast }
